import {
    ArchiveBoxIcon,
    DocumentTextIcon,
    QuestionMarkCircleIcon,
    ReceiptPercentIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { CreditCardStatementIcon } from '../../../components/general/CustomIcons/CreditCardStatementIcon';
import { useRef, useState } from 'react';
import { InfoTooltip } from '../../../components/general/InfoTooltip.tsx';
import Popup from '../../../components/general/Popup.tsx';
import { SecondaryButton } from '../../../components/general/SecondaryButton.tsx';
import { fieldLabelClasses } from '../../../components/general/fieldStyles.ts';
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts.ts';
import { usePendingHandoff } from '../DocumentDrawerProvider.tsx';
import { recategorisationShortcuts } from '../utils/recategorisationShortcuts.ts';
import {
    type RecategorizeCategoryType,
} from '../../../types/enums.ts';
import type { TFICDocument } from '../types/Document';
import { DeleteDocumentPopup } from './DeleteDocumentPopup.tsx';
import { DocumentPreview } from './DocumentPreview';

type RecategorisationViewProps = {
    document: TFICDocument;
    onRecategorize?: (category: RecategorizeCategoryType) => Promise<void>;
    onDelete?: () => Promise<void>;
    canArchive: boolean;
};

export function RecategorisationView({
    document,
    onRecategorize,
    onDelete,
    canArchive,
}: RecategorisationViewProps) {
    const [isRecategorizing, setIsRecategorizing] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [recategorizeTargetCategory, setRecategorizeTargetCategory] = useState<RecategorizeCategoryType | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const isHandingOff = usePendingHandoff() !== null;
    const isBusy = isRecategorizing || isDeleting || isHandingOff;

    const categoryInConfirmation = useRef<RecategorizeCategoryType | null>(null);
    if (recategorizeTargetCategory !== null) {
        categoryInConfirmation.current = recategorizeTargetCategory;
    }

    useKeyboardShortcuts({
        shortcuts: [
            { keys: ['Delete', 'Backspace'], handler: () => setShowDeleteConfirmation(true) },
            ...recategorisationShortcuts(canArchive).map(({ keys, category }) => ({
                keys,
                handler: () => setRecategorizeTargetCategory(category),
            })),
        ],
        enabled: Boolean(onDelete) && Boolean(onRecategorize) && !isBusy,
        ownerRef: containerRef,
    });

    const handleDeleteConfirm = async () => {
        if (onDelete) {
            setIsDeleting(true);
            try {
                await onDelete();
            } finally {
                setIsDeleting(false);
                setShowDeleteConfirmation(false);
            }
        }
    };

    const handleRecategorizeConfirm = async () => {
        if (onRecategorize && recategorizeTargetCategory) {
            setIsRecategorizing(true);
            try {
                await onRecategorize(recategorizeTargetCategory);
            } finally {
                setIsRecategorizing(false);
                setRecategorizeTargetCategory(null);
            }
        }
    };

    function getRecategorizeConfirmationPopupTitle(target: RecategorizeCategoryType | null): string {
        if (target == null) {
            return 'Documenttype bevestigen';
        }
        switch (target) {
            case 'invoice':
                return 'Markeren als factuur';
            case 'pos_receipt':
                return 'Markeren als bonnetje';
            case 'credit_card_statement':
                return 'Markeren als creditcard afschrift';
            case 'archive':
                return 'Naar archief verplaatsen';
            default: {
                const _exhaustive: never = target;
                return _exhaustive;
            }
        }
    }

    const bookingDocumentButtons = (
        <>
            <SecondaryButton
                title="Markeer als factuur"
                tooltip="Sneltoets 1"
                icon={DocumentTextIcon}
                onClick={() => setRecategorizeTargetCategory('invoice')}
                disabled={isBusy}
            />
            <SecondaryButton
                title="Markeer als bonnetje"
                tooltip="Sneltoets 2"
                icon={ReceiptPercentIcon}
                onClick={() => setRecategorizeTargetCategory('pos_receipt')}
                disabled={isBusy}
            />
            <SecondaryButton
                title="Markeer als creditcard afschrift"
                tooltip="Sneltoets 3"
                icon={CreditCardStatementIcon}
                onClick={() =>
                    setRecategorizeTargetCategory('credit_card_statement')
                }
                disabled={isBusy}
            />
        </>
    );

    const deleteButton = (
        <button
            onClick={() => setShowDeleteConfirmation(true)}
            disabled={isBusy}
            title="Sneltoets Delete of Backspace"
            className="rounded-md flex gap-2 items-center px-2.5 py-1.5 text-sm font-semibold bg-red-600 text-white shadow-sm hover:bg-red-500 disabled:bg-red-300 disabled:cursor-not-allowed"
        >
            <TrashIcon className="h-5 w-5 mb-0.5" />
            Verwijder
        </button>
    );

    return (
        <>
            <div ref={containerRef} className="w-full flex gap-4 p-4 h-full max-h-[85vh]">
                {(onRecategorize && onDelete) && (
                    <div className="w-1/2 flex flex-col items-center justify-center">
                        <QuestionMarkCircleIcon className="size-16 text-gray-400" />
                        <div className="text-gray-900 mb-8 mt-6 text-center">
                            <p className="text-2xl font-bold inline-flex items-center mb-2 ">
                                Dit document wordt momenteel niet herkend{' '}
                            </p>
                            <p className="text-sm opacity-90">
                                Kan je ons helpen om het juiste type document te kiezen?{' '}
                                <span className="ml-1 mb-2">
                                    <InfoTooltip
                                        text={
                                            'Als je geen actie onderneemt, blijft het document ook in de overig bak. Dit kan bijvoorbeeld voor documenten die niet facturen of bonnetjes zijn.'
                                        }
                                    />
                                </span>
                            </p>
                        </div>
                        {canArchive ? (
                            <div className="flex flex-col items-center gap-6">
                                <div className="flex flex-wrap justify-center gap-2">{bookingDocumentButtons}</div>
                                <div className="flex w-full items-center gap-3">
                                    <div className="h-px flex-1 bg-gray-200" />
                                    <span className="text-xs text-gray-400">of</span>
                                    <div className="h-px flex-1 bg-gray-200" />
                                </div>
                                <div className="flex flex-col items-center gap-2">
                                    <span className={fieldLabelClasses}>Geen boekingsdocument</span>
                                    <div className="flex flex-wrap justify-center gap-2">
                                        <SecondaryButton
                                            title="Naar archief"
                                            tooltip="Sneltoets 4"
                                            icon={ArchiveBoxIcon}
                                            onClick={() => setRecategorizeTargetCategory('archive')}
                                            disabled={isBusy}
                                        />
                                        {deleteButton}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3 justify-center">
                                <div className="flex gap-2">
                                    {bookingDocumentButtons}
                                    {deleteButton}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className="w-1/2 flex justify-center items-start overflow-y-auto">
                    <DocumentPreview document={document} />
                </div>
            </div>
            <Popup
                open={!!recategorizeTargetCategory}
                onClose={() => setRecategorizeTargetCategory(null)}
                onConfirm={handleRecategorizeConfirm}
                title={getRecategorizeConfirmationPopupTitle(categoryInConfirmation.current)}
                variant="default"
                confirmButtonDisplayLabel={
                    categoryInConfirmation.current === 'archive' ? 'Naar archief' : 'Bevestigen'
                }
                confirmButtonDisabled={isRecategorizing}
                confirmOnEnter
            >
                {categoryInConfirmation.current === 'archive' ? (
                    <div className="text-sm text-gray-700 space-y-3">
                        <p>
                            We bewaren dit document voor je in het archief, zodat je het later terug kunt vinden. Je
                            kunt het altijd opzoeken op de pagina Archief.
                        </p>
                        <p>
                            Het document wordt niet geboekt en verdwijnt uit de lijst met te controleren documenten.
                        </p>
                    </div>
                ) : (
                    <div className="text-sm text-gray-700 space-y-3">
                        <p>
                            {categoryInConfirmation.current === 'credit_card_statement' ? (
                                <>Bedankt voor je hulp! Het document wordt gemarkeerd als creditcard afschrift.</>
                            ) : (
                                <>
                                    Bedankt voor je hulp! We gaan het document opnieuw verwerken met deze nieuwe
                                    informatie.
                                </>
                            )}
                        </p>
                        <p>
                            {categoryInConfirmation.current === 'invoice' && (
                                <>
                                    Door aan te geven dat dit een factuur is, help je ons systeem om in de toekomst
                                    soortgelijke documenten beter te herkennen.
                                </>
                            )}
                            {categoryInConfirmation.current === 'pos_receipt' && (
                                <>
                                    Door aan te geven dat dit een bonnetje is, help je ons systeem om in de toekomst
                                    soortgelijke documenten beter te herkennen.
                                </>
                            )}
                            {categoryInConfirmation.current === 'credit_card_statement' && (
                                <>
                                    Door aan te geven dat dit een creditcard afschrift is, help je ons systeem om in de
                                    toekomst soortgelijke documenten beter te herkennen.
                                </>
                            )}
                        </p>
                        {categoryInConfirmation.current !== 'credit_card_statement' && (
                            <p>Het document wordt nu opnieuw gescand en de velden worden automatisch ingevuld.</p>
                        )}
                    </div>
                )}
            </Popup>
            <DeleteDocumentPopup
                open={showDeleteConfirmation}
                isDeleting={isDeleting}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDeleteConfirm}
            />
        </>
    );
}
